// Sitzung in Komponenten: wer bin ich, was darf ich, wo arbeite ich. Alles liest die Sitzungsantwort des Servers.
import { useCallback, useMemo, type ReactNode } from 'react'
import type { AnyActionContract, PermissionKey } from '@shared/contract'
import { setActiveLocation, useSessionStore, type OfflineStart } from '../stores/session'
import { useSessionOverride } from './context'
import {
  canRunAction,
  hasFeature,
  hasPermission,
  isWorkLocation,
  locationsWithPermission,
  type PermissionScope,
} from './permissions'
import type { SessionLocation, SessionPayload } from './types'

/**
 * Start ohne Server (docs/kasse-offline/SPEZIFIKATION.md §14.7): Pfad und Kontext der Probe, solange die Hülle mit der
 * Ersatz-Sitzung läuft – sonst null. Die Seite der Probe erkennt daran, dass sie ohne echte Sitzung gestartet ist
 * (keine Aktion des Servers, `useSession()` liefert die Ersatz-Sitzung). Mit der echten Sitzung wird es wieder null.
 */
export function useOfflineStart(): OfflineStart | null {
  const status = useSessionStore((state) => state.status)
  const offline = useSessionStore((state) => state.offline)
  return status === 'offline-till' && offline ? offline : null
}

/** Die Sitzung oder null (nicht angemeldet). Innerhalb des App-Rahmens ist sie immer gesetzt. */
export function useSession(): SessionPayload | null {
  const override = useSessionOverride()
  const stored = useSessionStore((state) => state.session)
  return override !== undefined ? override : stored
}

/**
 * Hat der Nutzer das Recht? Ohne zweite Angabe: am Arbeitsstandort (bei „Alle Standorte“: an irgendeinem).
 * `usePermission('stock.adjustment.create', locationId)` prüft einen bestimmten Standort, `'any'` irgendeinen,
 * `'all'` nur mandantenweite Rechte.
 */
export function usePermission(key: PermissionKey | undefined, location?: PermissionScope | null): boolean {
  const session = useSession()
  return hasPermission(session, key, location ?? 'active')
}

/** Ist der Funktionsschalter für den Betrieb an? */
export function useFeature(key: string | undefined): boolean {
  return hasFeature(useSession(), key)
}

/** Darf der Nutzer diese Aktion derzeit aufrufen (Recht, Funktionsschalter, Standort)? Für Schaltflächen. */
export function useCanRun(contract: AnyActionContract, location?: PermissionScope): boolean {
  return canRunAction(useSession(), contract, location ?? 'active')
}

/** Standorte, an denen ein Recht gilt – für Standortfelder in Formularen. */
export function usePermittedLocations(key: PermissionKey): SessionLocation[] {
  const session = useSession()
  return useMemo(() => locationsWithPermission(session, key).filter(isWorkLocation), [session, key])
}

export interface ActiveLocation {
  /** null = „Alle Standorte“ */
  location: SessionLocation | null
  /** Arbeitsorte des Nutzers (ohne Transit, Kommission, Verleih) */
  locations: SessionLocation[]
  /** wechselt den Arbeitsstandort auf dem Server; null = alle */
  setLocation: (locationId: string | null) => Promise<void>
}

/** Der Arbeitsstandort ist ein Kontext, kein Modus (design-system §3.4): Er füllt Filter und Formulare vor. */
export function useActiveLocation(): ActiveLocation {
  const session = useSession()
  const locations = useMemo(() => (session?.locations ?? []).filter(isWorkLocation), [session])
  const location = useMemo(
    () => session?.locations.find((entry) => entry.id === session.activeLocationId) ?? null,
    [session],
  )
  const setLocation = useCallback(async (locationId: string | null) => {
    await setActiveLocation(locationId)
  }, [])
  return { location, locations, setLocation }
}

export interface CanProps {
  /** Recht, das vorhanden sein muss */
  permission?: PermissionKey
  /** Standort-ID, 'any', 'all' oder 'active' (Standard) */
  location?: PermissionScope
  /** Funktionsschalter, der an sein muss */
  feature?: string
  /** Aktion, die der Nutzer aufrufen dürfen muss (ersetzt permission/feature) */
  action?: AnyActionContract
  /** stattdessen zeigen (Standard: nichts) */
  fallback?: ReactNode
  children: ReactNode
}

/** Zeigt den Inhalt nur mit Recht und Funktionsschalter: `<Can permission="pricing.price.update">…</Can>`. */
export function Can({ permission, location, feature, action, fallback = null, children }: CanProps) {
  const session = useSession()
  const allowed = action
    ? canRunAction(session, action, location ?? 'active')
    : hasFeature(session, feature) && hasPermission(session, permission, location ?? 'active')
  return <>{allowed ? children : fallback}</>
}
